import * as React from 'react';
import orderBy from 'lodash-es/orderBy';
import Modal, { ModalSize } from '../../misc/components/modal';
import { CoughAnalysisSetEvent, User, CoughAnalysisSetComment } from '../../misc/api/apiTypes';
import Loading from '../../misc/components/loading';
import Alert, { AlertCategoryEnum } from '../../misc/components/Alert';
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { CoughAnalysisSet, WebPortalClient } from 'webportalclient';
import { useWebPortalApiClient, useWebPortalLegacyClient, WebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { BasicTimeInput, TimeInput } from '../../cough/analyse/components/EventAddInputs';
import { TextBox } from "../../forms/informedv2/TextBox";
import { Form, useFormApi } from "informed";
import { DateTimeAccuracy, LocalizedDateTime } from '../../misc/components/LocalizedDateTime';
import { useHistory } from 'react-router-dom';
import { AddAnalysisSetCommentAsyncAction, JumpToTimePointAction, RemoveAnalysisSetCommentAsyncAction } from '../../cough/analyse/actions';
import { formatToHHmmss_ss, HHmmssToTotalSeconds } from "../../misc/timeFormatting";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { useSecurityContext } from '../../Core/SecurityContext';

export interface SendForVisualReviewModalProps {
    comments: CoughAnalysisSetComment[]
    analysisSetId: string;
    onClose: () => void;
    errorStatus: string;
    dispatch: any;
    coughAnalysisSet: CoughAnalysisSet;
    blindComments: boolean;
    modalState: any;
    onPointTime1Change: any;
    onPointTime2Change: any;
    onCommentChange: any;
    onCommentReplyChange: any;
    setModalState: any;
}

interface SendForVisualReviewForm {
    CommentValue: string;
}

declare const rootUrl;

export const SendForVisualReviewModalBodyForm = (props: SendForVisualReviewModalProps) => {
    const formApi = useFormApi<SendForVisualReviewForm>();
    const [error, setError] = React.useState<string | undefined>();
    const [submitting, setSubmitting] = React.useState(false);
    const { study } = useCurrentStudyConfigurationContext();
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [coughAnalysisSetComments, setCoughAnalysisSetComments] = React.useState<CoughAnalysisSetComment[]>();
    const [users, setUsers] = React.useState<User[]>();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [coughAnalysisSetVisualReview, setCoughAnalysisSetVisualReview] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [events, setEvents] = React.useState<CoughAnalysisSetEvent[]>();
    const [selectedUser, setSelectedUser] = React.useState<string>();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const history = useHistory();
    const [pointTime1, setPointTime1] = React.useState(0);
    const [pointTime2, setPointTime2] = React.useState(0);
    const [commentValue, setCommentValue] = React.useState('');
    const [replyToCommentId, setReplyToCommentId] = React.useState(null);
    const [selectedCommentId, setSelectedCommentId] = React.useState(null);

    React.useEffect(() => {
        const getUsers = async () => {
            legacyClient.getUsersByPermissionIncludingAllStudies(study.id, PermissionEnum.OpenAssignedCoughAnalysisSets)
                .then((usersResult) => {
                    if (usersResult.length === 0) {
                        setError("There are no users present with the 'Open Assigned Cough Analysis Sets' permission");
                    }

                    setUsers(usersResult.sort((a, b) => a.Username > b.Username ? 1 : -1));
                });
        }
        const getAnalysisSet = async () => {
            client.client.getCoughAnalysisSet(props.analysisSetId)
                .then((analysisSetResult) => {
                    if (analysisSetResult !== undefined) {
                        setCoughAnalysisSetVisualReview(analysisSetResult.visualReview);
                    }

                    setCoughAnalysisSetVisualReview(analysisSetResult.visualReview);
                    setLoading(false);
                });
        }
        getUsers();
        getAnalysisSet();
    }, []);

    const updateVisualReviewStatus = (analysisSetId: string) => {
        setSubmitting(true);

        legacyClient.updateVisualReviewStatus(analysisSetId)
            .then(json => {
                setSubmitting(false);
                props.onClose();
                var location = history.location;
                location.pathname = location.pathname.replace('false', 'true');
                history.replace(location);
                history.push("/cough/analysis")
            });
    }

    const handleReplyClick = (comment: CoughAnalysisSetComment) => {
        const isCurrentlySelected = props.modalState.selectedReplyId === comment.id;

        setSelectedCommentId(isCurrentlySelected ? null : comment.id);
        setReplyToCommentId(isCurrentlySelected ? null : comment.id);
        
        props.setModalState((prevState) => ({
            ...prevState,
            pointTime1: isCurrentlySelected ? 0: comment.commentStartTime,
            pointTime2: isCurrentlySelected ? 0 : comment.commentEndTime,
            selectedReplyId: isCurrentlySelected ? null : comment.id, 
        }));
    };

    const canDeleteComment = (currentUser: User, comment: CoughAnalysisSetComment) => {
        if (currentUserPermissions.some(x => x.Id === PermissionEnum.DeleteAnyCoughAnalysisSetComment))
        {
            return true;
        }
        else if (currentUser.Email === comment.userEmail) {
            if (props.coughAnalysisSet.visualReview === true && props.coughAnalysisSet.qaAnalyst === currentUser.Email && (props.coughAnalysisSet.visualInspectionStatusLastChanged === null || comment.created > props.coughAnalysisSet.visualInspectionStatusLastChanged))
                return true;
            else if (props.coughAnalysisSet.visualReview !== true && props.coughAnalysisSet.analyst === currentUser.Email && (props.coughAnalysisSet.visualInspectionStatusLastChanged === null || comment.created > props.coughAnalysisSet.visualInspectionStatusLastChanged))
                return true;
            else
                return false;
        }
        else
        {
            return false;
        }
    };


    const renderRow = (comment: CoughAnalysisSetComment, allComments: CoughAnalysisSetComment[], depth = 0) => {

        const childComments = allComments.filter(
            child => String(child.parentCommentId).toLowerCase() === String(comment.id).toLowerCase()
        );
        const orderedChildComments = orderBy(childComments, "created", "asc");
        const isParent = depth === 0;

        return (
            <React.Fragment key={`Comment${comment.id}`}>
                <tr key={`CommentRow${comment.id}`} style={{ borderTop: isParent ? "2px solid rgb(204, 204, 204)" : "1px solid #ccc"}}>
                    <td>{!isParent ? <i className="fa fa-reply" style={{ marginLeft: '5px' }} title="Reply" /> : (
                        <button
                            className="btn btn-primary btn-xs"
                            onClick={() => toggleChildComments(comment.id)}
                            style={{ marginLeft: '5px' }}
                        >
                            {hiddenChildComments[comment.id] ? <i className="fa fa-eye" title="Show Replies" /> : <i className="fa fa-eye-slash" title="Hide Replies" />}
                        </button>
                    )}</td>
                    <td
                        style={{ wordBreak: 'break-all', whiteSpace: 'nowrap' }}
                        data-automationid={`comments-modal-timestamp-${comment.id}`}
                    >
                        {comment.commentStartTime > 0 ? (
                            <span className="label label-primary" style={{ fontSize: '13px' }}>
                                {formatToHHmmss_ss(comment.commentStartTime)}
                            </span>
                        ) : "-"}
                        {comment.commentStartTime && (
                            <button
                                className="btn btn-primary btn-xs"
                                style={{ marginLeft: '1px' }}
                                onClick={() => JumpToTimepoint(props, comment.commentStartTime)}
                            >
                                <i className="fa fa-share" />
                            </button>
                        )}
                    </td>

                    <td
                        style={{ wordBreak: 'break-all', whiteSpace: 'nowrap' }}
                        data-automationid={`comments-modal-timestampend-${comment.id}`}
                    >
                        {comment.commentEndTime > 0 ? (
                            <span className="label label-primary" style={{ fontSize: '13px' }}>
                                {formatToHHmmss_ss(comment.commentEndTime)}
                            </span>
                        ) : "-"}
                        {comment.commentEndTime && (
                            <button
                                className="btn btn-primary btn-xs"
                                style={{ marginLeft: '1px' }}
                                onClick={() => JumpToTimepoint(props, comment.commentEndTime)}
                            >
                                <i className="fa fa-share" />
                            </button>
                        )}
                    </td>
                    <td style={{ wordBreak: 'keep-all', whiteSpace: 'pre-wrap' }} data-automationid={`comments-modal-comment-${comment.id}`}>{comment.comment}</td>
                    <td data-automationid={`Comments-modal-user-${comment.id}`}>{comment.userEmail}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                        <LocalizedDateTime automationId={`comments-modal-created-${comment.id}`} time={comment.created} accuracy={DateTimeAccuracy.Second} isTooltip={true} />
                    </td>
                    <td
                        style={{ wordBreak: 'break-all', whiteSpace: 'nowrap' }}
                        data-automationid={`comments-modal-timestampend-${comment.id}`}
                    >{ canDeleteComment(currentUser, comment) ?
                            <> <button
                            className="btn btn-danger btn-xs"
                            onClick={() => DeleteComment(props, client, comment)}
                        >
                                <i className="fa fa-trash" />
                            </button> &nbsp;</> : null}
                        {comment.parentCommentId == null ? <button
                            style={{ marginLeft: '1px' }}
                            className={`btn ${props.modalState.selectedReplyId === comment.id ? "btn-success" : "btn-primary"} btn-xs`}
                            onClick={() => handleReplyClick(comment)}
                        >
                            <i className="fa fa-reply" style={{ marginRight: '1px' }} />
                            Reply
                        </button>: null}
                    </td>
                </tr>
                
                {!hiddenChildComments[comment.id] && !hideComments &&
                    orderedChildComments.map((childComment) =>
                        renderRow(childComment, allComments, depth + 1)
                    )}
            </React.Fragment>
        );
    };

    const handleSubmit = async () => {
        if (props.modalState.commentValue.trim() === "") {
            setError("Comment is required");
        } else if (props.modalState.commentValue.length > 1000) {
            setError("Comment cannot exceed 1000 characters");
        } else if (props.modalState.pointTime2 > 0 && props.modalState.pointTime2 < props.modalState.pointTime1) {
            setError("Timestamp End value must be after Timestamp Start value");
        }
        else {
            setError("");
            props.dispatch(
                AddAnalysisSetCommentAsyncAction.create(client, props.analysisSetId, props.modalState.pointTime1 > 0 ? props.modalState.pointTime1 : props.modalState.pointTime2 > 0 ? 0.000001 : null, props.modalState.pointTime2 > 0 ? props.modalState.pointTime2 : null, props.modalState.commentValue, props.modalState.selectedReplyId))
            setCommentValue("");
            resetModalState();
        }
       
    };

    React.useEffect(() => {
        async function fetchData() {
            const data = await client.client.getCoughAnalysisSetComments(undefined, `analysisSetId eq ${props.analysisSetId}`);
            setCoughAnalysisSetComments(data.value);

        }
        fetchData();
    }, []);

    const [sortField, setSortField] = React.useState("created");
    const [sortDirection, setSortDirection] = React.useState("desc");


    const handleSort = (field) => {

        const direction = (field === sortField && sortDirection === "desc") ? "asc" : "desc";
        setSortField(field);
        setSortDirection(direction);
    };

    const renderSortIcon = (field) => {
        if (field === sortField) {
            return sortDirection === "asc" ? (
                <FontAwesomeIcon icon={faSortUp} />
            ) : (
                <FontAwesomeIcon icon={faSortDown} />
            );
        }
        return <FontAwesomeIcon icon={faSort} />;
    };

    const sortedComments = orderBy(props.comments, [sortField], [sortDirection]);

    const [hiddenChildComments, setHiddenChildComments] = React.useState({}); 
    const [hideComments, setHideComments] = React.useState(false); 

    const toggleChildComments = (parentId) => {
        setHiddenChildComments((prev) => ({
            ...prev,
            [parentId]: !prev[parentId], 
        }));
    };

    const resetModalState = () => {
        props.setModalState({
            pointTime1: 0,
            pointTime2: 0,       
            commentValue: "",
            selectedReplyId: null,
        });
    };

    const partialCommentBlinding = () => {
        if (!currentUserPermissions.some(x => x.Id === PermissionEnum.ViewAllCoughAnalysisSetComments)) {
            if (props.coughAnalysisSet.lastAnalyst != null && props.coughAnalysisSet.analyst != props.coughAnalysisSet.lastAnalyst) {
                const filteredComments = sortedComments.filter(c => c.created > props.coughAnalysisSet.analystLastChangedDateTime)
                if (sortedComments.length - filteredComments.length > 0) {
                    return (
                        <><tr><td colSpan={8}><Alert show={true} category={AlertCategoryEnum.Warning}>
                            Comment Blinding is Active on some Analysis Set Comments
                        </Alert></td></tr><tbody> {filteredComments
                            .filter(comment => comment.parentCommentId === null)
                            .map(comment => renderRow(comment, sortedComments))}</tbody></>)
                }
            }
        }

       return ( <tbody > {
            sortedComments
                        .filter(comment => comment.parentCommentId === null)
                .map(comment => renderRow(comment, sortedComments))
        }</tbody >)
    }

    const textareaStyle = error
        ? { border: "1px solid red" } 
        : {};

    return (<Modal show={true} size={ModalSize.Large} closeModalHandler={() => props.onClose()} header="Send For Review">
        <div className='modal-body'>
            {props.errorStatus ? <Alert category={AlertCategoryEnum.Danger} show={true}>{props.errorStatus}</Alert> : null}
            <table className="table">
                <thead style={{ borderBottom: "3px solid rgb(204, 204, 204)" }} >
                    <tr>
                        <th>Show/Hide Replies</th>
                        <th onClick={() => handleSort("commentStartTime")}>
                            Timestamp Start {renderSortIcon("commentStartTime")}
                        </th>
                        <th>Timestamp End</th>
                        <th>Comment</th>
                        <th>User</th>
                        <th onClick={() => handleSort("created")}>
                            Created {renderSortIcon("created")}
                        </th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>

                {props.blindComments && props.blindComments != null ? <tr><td colSpan={8}><Alert show={true} category={AlertCategoryEnum.Warning}>
                    Comment Blinding is Active on Analysis Set Comments
                </Alert></td></tr> : partialCommentBlinding()}
            </table>
            <p className="text-left"><b>{props.modalState.selectedReplyId ? "Reply to Selected Comment:" : "Add a Comment:"}</b></p>
            <div className="row">
                <div className="col-md-12">
                    <div className="form-group col-sm-3">
                        <p className="text-left">Timestamp Start:</p><BasicTimeInput id="pointTimeInput" labelText="Position" value={props.modalState.pointTime1} onChange={props.onPointTime1Change} onBlur={null} isEditMode={true} isValid={true} maxTimeValue={null} />
                    </div>
                    <div className="form-group col-sm-3">
                        <p className="text-left">Timestamp End (If Applicable):</p><BasicTimeInput id="pointTimeInput2" labelText="Position" value={props.modalState.pointTime2} onChange={props.onPointTime2Change} onBlur={null} isEditMode={true} isValid={true} maxTimeValue={null} />
                    </div>
                    <div className="form-group col-sm-6">
                        <textarea
                            className="form-control"
                            style={textareaStyle}
                            placeholder={props.modalState.selectedReplyId ? "Enter Reply" : "Enter Comment"}
                            autoFocus={true}
                            value={props.modalState.commentValue}
                            onChange={props.onCommentChange}
                            rows={5}
                            cols={40}
                        ></textarea>
                        {error && <div style={{ color: "red", marginTop: "5px" }}>{error}</div>}
                        <button className="btn btn-primary" type="button" disabled={disabled} data-automationid="SendForReviewAddCommentButton" style={{ marginTop: '10px' }}
                            onClick={handleSubmit}>
                            {props.modalState.selectedReplyId ? "Add Reply" : "Add Comment"}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div className='modal-footer'>
            {!loading && coughAnalysisSetVisualReview !== true && props.coughAnalysisSet.visualReviewAnalystSelectionId !== 0 && props.coughAnalysisSet.visualReviewAnalystSelectionId !== 1 && (
                <button disabled={submitting} className='btn btn-primary' onClick={() => updateVisualReviewStatus(props.analysisSetId)}>Send For Review</button>)}
            <button className='btn btn-outline btn-primary' onClick={() => props.onClose()}>Close</button>
        </div>
    </Modal>);
}

export const SendForVisualReviewModalBody = (props: SendForVisualReviewModalProps) => {
    return (
        <Form>
            <SendForVisualReviewModalBodyForm  {...props} />
        </Form>
    );
}

const JumpToTimepoint = (props: SendForVisualReviewModalProps, commentStartTime: number) => {
    props.dispatch(
        JumpToTimePointAction.create(
            commentStartTime
        )
    );
    props.onClose()
}

const DeleteComment = (props: SendForVisualReviewModalProps, client: WebPortalApiClient, comment: CoughAnalysisSetComment) => {
    props.dispatch(
        RemoveAnalysisSetCommentAsyncAction.create(
            client,
            comment.id,
            comment.analysisSetId
        )
    );
}


